import {
  Button,
  CopyToClipboard,
  FieldSelect,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Page,
} from '@dixa/lib-shared-habla';
import type { WidgetT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { AppLink } from 'components/appLink';
import { CodeHighlight } from 'components/codeHighlight';
import { withWidgets, WithWidgetsInjectedPropsT } from 'hocs';
import { useCopyWithDelay } from 'hooks/useCopyWithDelay';
import { useNotifications } from 'hooks/useNotifications';

import chatWidgetRawHTMLScript from './Snippet.html';

type PropsT = WithWidgetsInjectedPropsT;

const TEMPLATE_OPTIONS = [
  { id: '{{agent_firstname}}', name: 'First Name' },
  { id: '{{agent_lastname}}', name: 'Last Name' },
  { id: '{{agent_displayname}}', name: 'Display Name' },
];

const ChatWidgetSettingsImpl = (props: PropsT): JSX.Element => {
  const notify = useNotifications();
  const params = useParams<'chatWidgetId'>();
  const [widget, setWidget] = useState<WidgetT | null>(null);
  const { isCopied, setIsCopied } = useCopyWithDelay(false);

  useMount(() => {
    const { widgets } = props;
    const { chatWidgetId } = params;

    if (chatWidgetId) {
      widgets
        .getItem(chatWidgetId)
        .then((res) => {
          setWidget(res.data);
        })
        .catch(() => {
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to fetch widget`,
            icon: 'exclamation',
            className: 'danger',
          });
        });
    }
  });

  const updateWidget = (agentNameTemplate?: string) => {
    const { widgets } = props;

    if (widget && agentNameTemplate) {
      const { id, name } = widget;

      widgets
        .update(id, {
          updates: {
            agent_name_template: agentNameTemplate,
          },
        })
        .then(() => {
          notify({
            title: `Widget ${name}`,
            text: `"${name}" widget has been updated`,
            icon: 'done',
            className: 'success',
            time: 5000,
          });
        })
        .catch(() => {
          notify({
            title: 'An error occurred',
            text: `An error occurred when trying to update the "${name}" widget`,
            icon: 'exclamation',
            className: 'danger',
          });
        });
    }
  };

  const copyCodeSnippet = () => {
    ampli.copyScript();
    setIsCopied(true);
  };

  const getCodeSnippet = (html = '') => {
    return { __html: widget ? html.replace('%WIDGET_ID%', widget.id) : '' };
  };

  const chatWidgetScript = getCodeSnippet(chatWidgetRawHTMLScript);
  const agentNameTemplate =
    widget?.agent_name_template ?? '{{agent_displayname}}';

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/chat" style={{ color: 'inherit' }}>
              Chat
            </AppLink>

            <Icon name="angle-right" />

            {`Setup${widget && ` - ${widget.name}`}`}
          </FlexBox>
        }
      />

      <Page.Body>
        <Page.Section>
          <FormGrid>
            <FormField>
              <FieldSelect
                meta={{}}
                input={{
                  name: 'agentNameTemplate',
                  onChange: updateWidget,
                  value: agentNameTemplate,
                }}
                label="What agent name should be displayed to users in this chat widget?"
                items={TEMPLATE_OPTIONS}
              />
            </FormField>

            <FormField>
              <FieldWrapper
                label="Place this script in to your website's HTML source code"
                helpIcon={{
                  text: 'This code must be inserted into the section of every single page on which you wish to display the Dixa widget.',
                }}
                noAlert
              >
                <CodeHighlight
                  languages={['html', 'js']}
                  className="js-snippit-highlight"
                >
                  {chatWidgetScript.__html}
                </CodeHighlight>
              </FieldWrapper>
            </FormField>

            <CopyToClipboard
              onClick={copyCodeSnippet}
              textToCopy={chatWidgetScript.__html}
              tooltip={false}
            >
              <Button>{isCopied ? 'Copied' : 'Copy script'}</Button>
            </CopyToClipboard>
          </FormGrid>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};

export const ChatWidgetSettings = withWidgets(ChatWidgetSettingsImpl);
